import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import { getAxiosInstance } from '@/services/axiosConfig';
import { log } from '@/logger/logger';
import { HandleType } from '@/enums/handle-type.enum';
import { I18N_KEYS } from '@/language/keys';
import { FF_COMPARISON_NODE, FF_POINTCLOUD_FROM_DEPTH } from '@/consts/featureFlags';
import { useNodeFiltersStore } from '@/state/nodes/nodes.state';
import { parseMenuData } from '@/state/nodes/nodes-parser';
import type { TFunction } from 'i18next';

const logger = log.getLogger('useFetchMenuItems');
const axiosInstance = getAxiosInstance();

const getIteratorMenuCategory = (t: TFunction) => ({
  id: 'iterators',
  order: 35,
  children: [
    {
      id: 'text_iterator',
      displayName: t(I18N_KEYS.RECIPE_MAIN.FLOW.LEFT_PANEL.ITERATOR_MENU_ITEMS.TEXT_ITERATOR.DISPLAY_NAME),
      description: t(I18N_KEYS.RECIPE_MAIN.FLOW.LEFT_PANEL.ITERATOR_MENU_ITEMS.TEXT_ITERATOR.DESCRIPTION),
      icon: 'text-iterator',
      inputTypes: [HandleType.Array],
      outputTypes: [HandleType.Text],
      isLeaf: true,
      price: 0,
      searchText: 'text-iterator iterator text',
      isTool: true,
    },
  ],
});

const createImageIteratorMenuItem = (t: TFunction) => ({
  id: 'image_iterator',
  displayName: t(I18N_KEYS.RECIPE_MAIN.FLOW.LEFT_PANEL.ITERATOR_MENU_ITEMS.IMAGE_ITERATOR.DISPLAY_NAME),
  description: t(I18N_KEYS.RECIPE_MAIN.FLOW.LEFT_PANEL.ITERATOR_MENU_ITEMS.IMAGE_ITERATOR.DESCRIPTION),
  icon: 'image-iterator',
  inputTypes: [],
  outputTypes: [HandleType.Image],
  isLeaf: true,
  price: 0,
  searchText: 'image-iterator iterator image',
  isTool: true,
});

const createVideoIteratorMenuItem = (t: TFunction) => ({
  id: 'video_iterator',
  displayName: t(I18N_KEYS.RECIPE_MAIN.FLOW.LEFT_PANEL.ITERATOR_MENU_ITEMS.VIDEO_ITERATOR.DISPLAY_NAME),
  description: t(I18N_KEYS.RECIPE_MAIN.FLOW.LEFT_PANEL.ITERATOR_MENU_ITEMS.VIDEO_ITERATOR.DESCRIPTION),
  icon: 'video-iterator',
  inputTypes: [],
  outputTypes: [HandleType.Video],
  isLeaf: true,
  price: 0,
  searchText: 'video-iterator iterator video',
  isTool: true,
});

const createComparisonMenuItem = () => ({
  id: 'comparison',
  displayName: 'Compare',
  description: 'Compare two inputs with separate outputs',
  icon: 'comparison',
  inputTypes: [HandleType.Image],
  outputTypes: [HandleType.Image],
  isLeaf: true,
  price: 0,
  searchText: 'compare comparison side-by-side diff',
  isTool: true,
  isNew: true,
});

const createPointCloudFromDepthMenuItem = () => ({
  id: 'point_cloud_from_depth',
  displayName: 'Point Cloud',
  description: 'Convert depth image to point cloud',
  icon: 'point-cloud-from-depth',
  inputTypes: [HandleType.Image],
  outputTypes: [HandleType.Image],
  isLeaf: true,
  price: 0,
  searchText: 'point cloud depth',
  isTool: true,
  isNew: true,
});

const createKlingElementMenuItem = () => ({
  id: 'kling_element',
  displayName: 'Kling Element',
  description: 'Provide structured element data for the Kling model',
  icon: 'kling',
  inputTypes: [HandleType.Image],
  outputTypes: [HandleType.KlingElement],
  isLeaf: true,
  price: 0,
  searchText: 'kling element reference frontal image',
  isTool: true,
  isNew: true,
});

export const useFetchMenuItems = () => {
  const { t } = useTranslation();
  const isComparisonNodeEnabled = useFeatureFlagEnabled(FF_COMPARISON_NODE);
  const isPointCloudFromDepthEnabled = useFeatureFlagEnabled(FF_POINTCLOUD_FROM_DEPTH);
  const { setMenuData, setIsMenuDataLoaded } = useNodeFiltersStore();

  const fetchMenuItems = useCallback(async () => {
    try {
      const res = await axiosInstance.get(`/v1/node-definitions/menu`);
      const iteratorMenuCategory = getIteratorMenuCategory(t);
      res.data.menu.tools.push(iteratorMenuCategory);
      iteratorMenuCategory.children.push(createImageIteratorMenuItem(t));
      iteratorMenuCategory.children.push(createVideoIteratorMenuItem(t));

      if (res.data.menu.tools) {
        const helpersCategory = res.data.menu.tools.find((cat: any) => cat.id === 'helpers');
        if (helpersCategory?.children) {
          if (isComparisonNodeEnabled) {
            helpersCategory.children.push(createComparisonMenuItem());
          }
          if (isPointCloudFromDepthEnabled) {
            helpersCategory.children.push(createPointCloudFromDepthMenuItem());
          }
          helpersCategory.children.push(createKlingElementMenuItem());
        }
      }

      const parsedMenuData = parseMenuData(res.data);

      setMenuData(parsedMenuData);
    } catch (e) {
      logger.error('Error fetching menu items', e);
    } finally {
      setIsMenuDataLoaded(true);
    }
  }, [setMenuData, setIsMenuDataLoaded, t, isComparisonNodeEnabled, isPointCloudFromDepthEnabled]);

  return { fetchMenuItems };
};
